import classNames from 'classnames';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { cn, getIndexedTitle } from '../common/utils';
import { Button } from '../components/actions/Button';
import { PanelHeader } from '../components/designer/panels/PanelHeader';
import * as Icon from '../components/icons';
import InboxZero from '../components/InboxZero';
import { Container } from '../components/layout/ContainerFlex';
import { Spacer } from '../components/layout/Spacer';
import LoadingIndicator from '../components/LoadingIndicator';
import QueryTag from '../components/QueryTag';
import PanelContainer from '../containers/PanelContainer';
import { simulationShowDatasetAsTable } from '../simulation/store/simulation.state';
import CustomQueryDataTable from './CustomQueryDataTable';
import CustomQueryEditor from './CustomQueryEditor';
import DatasetQuerySaveButton from './DatasetQuerySaveButton';
import DatasetQuerySelector from './DatasetQuerySelector';
import {
  customQueryDataTableHeight,
  customQueryDescriptorById,
  customQueryDescriptorsAll,
  customQueryErrors,
} from './store/customQuery.state';

type CustomQueryComponentProps = {
  queryId: string;
  placeholder?: string;
  datasetId?: string;
};

const CustomQueryComponent: React.FC<CustomQueryComponentProps> = props => {
  const { t } = useTranslation('app');
  const [queryError, setQueryError] = useRecoilState(
    customQueryErrors(props.queryId),
  );
  const descriptor = useRecoilValue(customQueryDescriptorById(props.queryId));

  const [height, setHeight] = useRecoilState(customQueryDataTableHeight);

  const [showDatasetTable, setShowDatasetTable] = useRecoilState(
    simulationShowDatasetAsTable,
  );

  const [insertHelper, setInsertHelper] = useState<
    ((value: string) => void) | null
  >(null);

  const handleHelperSelect = (value: string) => {
    if (insertHelper) {
      insertHelper(value);
    }
  };

  const handleHelperInsert = (helper: (value: string) => void) => {
    setInsertHelper(() => helper);
  };

  // Only show data table if query has content
  const hasQueryContent = !_.isEmpty(descriptor?.sql?.trim());

  return (
    <>
      <Container
        col
        componentName={`QueryDatasetView`}
        overflow
        className={classNames(
          height !== 'h-min' ? 'min-h-20' : '',
          'border-app-panel-dark ltr:border-l rtl:border-r',
          'relative',
        )}
      >
        <Suspense
          fallback={
            <LoadingIndicator
              className="absolute left-0 right-0 top-[45%] m-auto max-h-28 max-w-10 shadow-2xl"
              absolute
              selfCenter
              backdrop
              message={t`Loading Data...`}
            />
          }
        >
          <Container className={height !== 'h-min' ? 'min-h-1/2 h-full' : ''}>
            <CustomQueryEditor
              placeholder={props.placeholder}
              queryDescriptorId={props.queryId}
              onHelperInsert={handleHelperInsert}
            />
          </Container>

          {hasQueryContent && (
            <>
              <PanelHeader
                title={t`Data`}
                closable={true}
                onCloseClick={() => setShowDatasetTable(null)}
                adjustable={!_.isNil(height)}
                fixedHeight={height}
                onHeightChange={setHeight}
              />
              {height !== 'h-min' && (
                <CustomQueryDataTable queryDescriptorId={props.queryId} />
              )}
            </>
          )}
        </Suspense>
      </Container>
    </>
  );
};

export type CustomQueryRootProps = {
  actionBefore?: React.ReactNode;
  actionAfter?: React.ReactNode;
  placeholder?: string;
  datasetId?: string;
};

const CustomQueryRoot: React.FC<CustomQueryRootProps> = props => {
  const { t } = useTranslation('app');
  const [activeQueryId, setActiveQueryId] = useState(null);

  const [descriptorsAll, setDescriptorsAll] = useRecoilState(
    customQueryDescriptorsAll,
  );

  const descriptorsArray = _.values(descriptorsAll);
  const titlePrefix = t`Query...`;

  // Add useEffect to automatically add a new query if none exists
  useEffect(() => {
    if (_.isEmpty(descriptorsAll)) {
      addQueryDescriptor();
    }
  }, []);

  function addQueryDescriptor() {
    const titles = new Set(
      _(descriptorsAll)
        .map(v => v.title)
        .uniq()
        .value(),
    );
    const id = nanoid(8);
    const title = getIndexedTitle(titles, titlePrefix);

    setDescriptorsAll(current => ({
      ...current,
      [id]: {
        id,
        title,
        sql: '',
      },
    }));
    setActiveQueryId(id);
  }

  function removeQueryDescriptor(id: string) {
    if (activeQueryId === id) {
      //deselect
      const currentIndex = _.findIndex(descriptorsArray, d => d.id === id);
      if (currentIndex > 0) {
        setActiveQueryId(descriptorsArray[currentIndex - 1].id);
      }
      if (currentIndex === 0) {
        setActiveQueryId(descriptorsArray[currentIndex + 1]?.id ?? '');
      }
    }

    setDescriptorsAll(current => _.omit(current, id));
  }

  return (
    <>
      <PanelContainer
        id={`custom-queries-root`}
        // collapsible
        isAccordion
        className="flex-1"
        classNameChildren="flex flex-col"
        title={
          <div className={cn('flex flex-1 items-center', 'gap-1')}>
            {props.actionBefore}

            {
              // list existing
              _.map(descriptorsAll, d => (
                <div
                  key={`custom-query-container-${d.id}`}
                  className="flex items-center gap-1"
                  onClick={e => {
                    e.stopPropagation();
                    setActiveQueryId(d.id);
                  }}
                >
                  <QueryTag
                    title={d.title}
                    removable
                    onRemove={() => removeQueryDescriptor(d.id)}
                    enabled={activeQueryId === d.id}
                  />
                </div>
              ))
            }

            <div
              className={cn(
                'flex items-center gap-1',
                'hover:text-menu-active',
                'mx-0.5 p-1',
              )}
              onClick={e => {
                e.stopPropagation();
                addQueryDescriptor();
              }}
            >
              <Icon.CirclePlus className={cn('h-5 w-5', 'fill-current')} />
              {/* {`Add`} */}
            </div>
            {props.actionAfter}
            <Spacer fullWidth />
            <DatasetQuerySelector
              viewMode="minimal"
              selectedId={descriptorsAll[activeQueryId]?.datasetQueryId}
              onChange={datasetQuery => {
                if (descriptorsAll[datasetQuery.id]) {
                  setActiveQueryId(datasetQuery.id);
                } else {
                  setDescriptorsAll(current => ({
                    ...current,
                    [datasetQuery.id]: {
                      id: datasetQuery.id,
                      title: datasetQuery.title,
                      sql: datasetQuery.query,
                      datasetQueryId: datasetQuery.id,
                    },
                  }));
                  setActiveQueryId(datasetQuery.id);
                }
              }}
            />
            <DatasetQuerySaveButton
              id={descriptorsAll[activeQueryId]?.datasetQueryId}
              getPatch={() => {
                const qd = descriptorsAll[activeQueryId];
                return {
                  title: qd.title,
                  query: qd.sql,
                };
              }}
            />
          </div>
        }
      >
        {_.isEmpty(activeQueryId) && (
          <InboxZero selfCenter message={t`Click + to add custom query`}>
            <Button
              label={t`Add Query`}
              className={cn('rounded ltr:ml-4 rtl:mr-4')}
              buttonSize="xs"
              buttonType="primary"
              hasIconAfter={
                <Icon.CirclePlus className={cn('h-5 w-5 fill-current')} />
              }
              onPress={() => addQueryDescriptor()}
            />
          </InboxZero>
        )}
        {!_.isEmpty(activeQueryId) && (
          <CustomQueryComponent
            placeholder={props.placeholder}
            queryId={activeQueryId}
            datasetId={props.datasetId}
          />
        )}
      </PanelContainer>
    </>
  );
};

export default CustomQueryRoot;
